import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ThresholdFields from "./ThresholdFields";
import AlertSettings from "./AlertSettings";
import ContactAccessTable from "./ContactAccessTable";
import { todayIso } from "./claimIntents";

function Row({ label, value }) {
  const empty = value == null || String(value).trim() === "";
  return (
    <Box display="flex" gap={2} mb={0.75} flexWrap="wrap">
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 170 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={empty ? { color: "error.main", fontWeight: 600 } : undefined}>
        {empty ? "Required - missing" : String(value)}
      </Typography>
    </Box>
  );
}

function Heading({ children }) {
  return (
    <Typography variant="subtitle2" fontWeight={700} mt={2} mb={1}>
      {children}
    </Typography>
  );
}

/**
 * Device assignment review shown inside a claim-mode customer review, before
 * the final attestations. Values are pre-submit selections ("Will be
 * assigned"); the completion summary shows the server-confirmed result.
 */
export default function DeviceAssignmentSection({
  deviceId,
  customer,
  addressText,
  assetOptions = [],
  assetValue,
  onAssetChange,
  assetIdentifier,
  assetDetails,
  providerNames = [],
  deviceName,
  onDeviceName,
  installationDate,
  onInstallationDate,
  thresholds = [],
  onThreshold,
  alerts,
  onAlerts,
  destinations = [],
  accessRows = [],
  onAccess,
  attest,
  onAttest,
  termsVersion,
  blockers = [],
  disabled = false,
}) {
  const multiple = assetOptions.length > 1;
  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={0.5}>
        Device assignment
      </Typography>
      <Typography variant="body2" mb={2} sx={{ opacity: 0.8 }}>
        Status: <strong>Will be assigned</strong>. Confirming saves the customer first, then assigns the device.
      </Typography>
      <Row label="Device" value={deviceId ? `Device #${deviceId}` : ""} />
      <Row label="Customer" value={customer?.name === "Required - missing" ? "" : customer?.name} />
      <Row label="Company / Status" value={[customer?.company, customer?.status].filter((v) => v && v !== "Required - missing").join(" / ")} />
      <Row label="Service address" value={addressText} />
      {multiple ? (
        <Box my={1.5}>
          <TextField
            select
            required
            fullWidth
            label="Assigned asset"
            value={assetValue || ""}
            onChange={(event) => onAssetChange(event.target.value)}
            disabled={disabled}
            helperText="This customer has more than one asset. Choose the one this device is installed on."
          >
            {assetOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      ) : (
        <Row label="Assigned asset" value={assetOptions[0]?.label || ""} />
      )}
      <Row label="Asset identifier" value={assetIdentifier} />
      {assetDetails ? <Row label="Asset details" value={assetDetails} /> : null}
      <Row
        label="Provider sharing"
        value={providerNames.length ? `${providerNames.join(", ")} - selected; saved status shown after submission` : "Not shared"}
      />

      <Divider sx={{ my: 2 }} />
      <Box display="grid" gap={2} gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}>
        <TextField
          required
          label="Device name"
          value={deviceName}
          onChange={(event) => onDeviceName(event.target.value)}
          disabled={disabled}
        />
        <TextField
          required
          type="date"
          label="Installation date"
          value={installationDate}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: todayIso() }}
          onChange={(event) => onInstallationDate(event.target.value)}
          disabled={disabled}
        />
      </Box>

      <Heading>Thresholds</Heading>
      <ThresholdFields rows={thresholds} onChange={onThreshold} />

      <Heading>Alert settings</Heading>
      <AlertSettings alerts={alerts} destinations={destinations} onChange={onAlerts} thresholds={thresholds} />

      <Heading>Contact device access</Heading>
      <ContactAccessTable rows={accessRows} onChange={onAccess} />

      <Divider sx={{ my: 2 }} />
      <Alert severity="info" sx={{ borderRadius: "12px", mb: 1 }}>
        The existing plan and verified included term follow this device. The confirmed installation date anchors the
        term; future billing responsibility follows the backend subscription policy.
      </Alert>
      <FormControlLabel
        control={<Checkbox checked={Boolean(attest?.accuracy)} onChange={(e) => onAttest({ ...attest, accuracy: e.target.checked })} disabled={disabled} />}
        label="I confirm the customer, site, asset, and installation information is accurate"
      />
      <FormControlLabel
        control={<Checkbox checked={Boolean(attest?.terms)} onChange={(e) => onAttest({ ...attest, terms: e.target.checked })} disabled={disabled || !termsVersion} />}
        label={termsVersion ? `I accept the installation terms (version ${termsVersion})` : "Installation terms are loading…"}
      />
      {blockers.length > 0 && (
        <Alert severity="warning" sx={{ borderRadius: "12px", mt: 1 }}>
          {blockers.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </Alert>
      )}
    </Box>
  );
}
